import type { ComponentChildren } from 'preact';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

const VARIANTS: Record<BadgeVariant, string> = {
  default: 'bg-surface-mid text-gray-300',
  success: 'bg-green-900/50 text-green-300',
  warning: 'bg-yellow-900/50 text-yellow-300',
  danger: 'bg-red-900/50 text-red-300',
  info: 'bg-blue-900/50 text-blue-300',
};

interface BadgeProps {
  variant?: BadgeVariant;
  children: ComponentChildren;
  className?: string;
}

export function Badge({ variant = 'default', children, className = '' }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${VARIANTS[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

const STATUS_MAP: Record<string, BadgeVariant> = {
  confirmed: 'success',
  completed: 'success',
  active: 'success',
  pending: 'warning',
  awaiting_payment: 'warning',
  checked_out: 'info',
  checked_in: 'info',
  cancelled: 'danger',
  expired: 'danger',
  failed: 'danger',
};

/**
 * The customer's words for a status, where the enum's own would mislead. `checked_out` is the
 * staff term for "the car has left with the customer", but customers read it the hotel way — as
 * "my booking has ended" — and asked support why a rental they were still driving said so. Any
 * status not listed here is shown as the enum with its underscores removed.
 */
const STATUS_LABELS: Record<string, string> = {
  checked_out: 'in use',
  checked_in: 'returned',
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={STATUS_MAP[status] ?? 'default'}>
      {STATUS_LABELS[status] ?? status.replace(/_/g, ' ')}
    </Badge>
  );
}
